import { Fragment, Suspense, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Sun,
  Moon,
  Monitor,
  AlertCircle,
  Mail,
  User,
  Palette,
  Settings,
  Bell,
  KeyRound,
  ArrowRight,
} from 'lucide-react';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { CompanySsoCard } from './CompanySsoCard';
import { NotificationsSettingsCard } from './NotificationsSettingsCard';
import { NavStylePicker } from './NavStylePicker';
import { SessionSortPicker } from './SessionSortPicker';
import { Separator } from '@reflex/ui/components/ui/separator';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { Alert, AlertDescription } from '@reflex/ui/components/ui/alert';
import { EMAIL_VERIFIED_QUERY_PARAM, EMAIL_VERIFY_FAILED_AUTH_ERROR } from '@reflex/shared';
import {
  EmailNotVerifiedNotice,
  EmailVerificationResultNotice,
} from '@/components/auth/EmailNotVerifiedNotice';
import { ProviderAccountList } from '@/components/integration/ProviderAccountList';
import type { LinkedAccount, ProviderEntry } from '@/components/integration/provider-state';
import { RequiredConfigurationBanner } from '@/components/user-onboarding/RequiredConfigurationBanner';
import { ThemePicker } from '@reflex/plugin-themes/web/ThemePicker';
import { useTheme } from 'next-themes';
import { mutateWithToast } from '@/lib/mutation-helpers';
import { makeInvalidatingMutation } from '@reflex/ui/client/mutation-factories';
import { cn } from '@reflex/ui/lib/utils';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { ROUTES } from '@/lib/routes';
import { useAuth } from '@/hooks/auth/useAuth';
import { useProviderConnect } from '@/hooks/auth/useProviderConnect';
import { showProviderConnectionError } from '@/lib/auth-provider-error';
import { useShowHiddenProviders } from '@/hooks/auth/useShowHiddenProviders';
import { useColorTheme } from '@reflex/plugin-themes/web/useColorTheme';
import { useNavStyle } from '@/components/layout/NavBar/useNavStyle';
import { useSessionSort } from '@/components/agent/useSessionSort';
import { usePlugins } from '@/hooks/config/usePlugins';
import {
  useUserOnboardingState,
  useUserOnboardingBannerDismiss,
} from '@/hooks/user-onboarding/useUserOnboardingState';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { PanelTitle, PageDescription } from '@reflex/ui/components/layout/Typography';
import { IconSectionHeader } from '@/components/shared/IconSectionHeader';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { SectionNav } from '@reflex/ui/components/layout/SectionNav';

type ThemeOption = 'light' | 'dark' | 'system';

const THEME_OPTIONS: { value: ThemeOption; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'Auto', icon: Monitor },
];

/**
 * Identity-first unlink. Targets the `auth_identities` row by its
 * primary id so sign-in-only providers (no stored token) can be
 * removed from the list the same way as token-bearing providers.
 * Drops the corresponding `provider_accounts` row server-side.
 */
const useUnlinkIdentityMutation = makeInvalidatingMutation(
  queryKeys.userIdentities,
  (identityId: string) => api.unlinkIdentity(identityId),
);

/** Plugin settings sections merged into the built-in Notifications group. */
const NOTIFICATION_SECTION_IDS = new Set(['slack-notifications']);

export function SettingsPage() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { themeId: colorTheme, setTheme: setColorTheme, themes: colorThemes } = useColorTheme();
  const [navStyle, setNavStyle] = useNavStyle();
  const [sessionSort, setSessionSort] = useSessionSort();
  // `user` comes from the auth provider rather than a second `/auth/me`
  // query. The provider already refetches on `visibilitychange`, and the
  // app's `refetchOnWindowFocus` would have made this page issue two
  // `/auth/me` reads per tab switch for the same data.
  const { providers: configuredProviders, user } = useAuth();
  const { settingsSections } = usePlugins();
  const { connect, connectingProvider } = useProviderConnect();
  const navigate = useNavigate();

  // Identity-first source: `auth_identities` is the canonical list of
  // OAuth identities (sign-in-only providers without a stored token
  // still appear here). `getUserProviders` is kept as the secondary
  // source for the `reauth` state — a missing token is detectable only
  // via the OAuth-token store, not via the identity row.
  const identitiesQuery = useQuery({
    queryKey: queryKeys.userIdentities,
    queryFn: api.getUserIdentities,
  });

  const providersQuery = useQuery({
    queryKey: queryKeys.userProviders,
    queryFn: api.getUserProviders,
  });

  const { data: onboardingState } = useUserOnboardingState();
  const bannerDismiss = useUserOnboardingBannerDismiss();

  const unlinkMutation = useUnlinkIdentityMutation();

  const { hash, pathname, search } = useLocation();

  // A signed-in user can still enter a provider's full-page sign-in route
  // (for example from an old bookmark). If that provider identity belongs to
  // another Reflex user, the callback returns here rather than dropping an
  // ignored error on the authenticated app root. Idempotent: clear the params
  // after showing the recovery actions so reload does not replay them.
  useEffect(() => {
    if (!search) return;
    const params = new URLSearchParams(search);
    const authError = params.get('auth_error');
    const provider = params.get('auth_provider');
    const configuredProvider = provider ? configuredProviders[provider] : undefined;
    if (authError && provider && configuredProvider) {
      showProviderConnectionError({
        provider,
        providerLabel: configuredProvider.label,
        error: authError,
        onRetry: () => void connect(provider),
      });
      params.delete('auth_error');
      params.delete('auth_provider');
      const next = params.toString();
      navigate({ pathname, search: next ? `?${next}` : '', hash }, { replace: true });
    }
  }, [configuredProviders, connect, hash, navigate, pathname, search]);

  // The verification link in the email lands here with the outcome in the
  // query string (`?email_verified=1` or `?auth_error=email_verify_failed`).
  // Read it once on mount, then strip it from the URL so a reload does not
  // claim a second verification.
  const [verificationOutcome] = useState<'verified' | 'failed' | null>(() => {
    const params = new URLSearchParams(search);
    if (params.get(EMAIL_VERIFIED_QUERY_PARAM) === '1') return 'verified';
    if (params.get('auth_error') === EMAIL_VERIFY_FAILED_AUTH_ERROR) return 'failed';
    return null;
  });
  useEffect(() => {
    if (!search) return;
    const params = new URLSearchParams(search);
    const verified = params.has(EMAIL_VERIFIED_QUERY_PARAM);
    const failed = params.get('auth_error') === EMAIL_VERIFY_FAILED_AUTH_ERROR;
    if (!verified && !failed) return;
    params.delete(EMAIL_VERIFIED_QUERY_PARAM);
    if (failed) {
      params.delete('auth_error');
      params.delete('reason');
    }
    const next = params.toString();
    navigate({ pathname, search: next ? `?${next}` : '', hash }, { replace: true });
  }, [hash, navigate, pathname, search]);

  // Handle hash-based scroll to provider rows (e.g. #provider-row-github).
  // Re-run when the URL hash changes so in-app navigations like
  // /settings#provider-row-google → /settings#provider-row-github reposition
  // even though the path is the same.
  useEffect(() => {
    const target = hash.startsWith('#') ? hash.slice(1) : hash;
    if (!target) return;

    const element = document.getElementById(target);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus();
    }
  }, [providersQuery.data, hash]);

  const providerAccounts = providersQuery.data ?? [];
  const identities = identitiesQuery.data?.identities ?? [];
  // Merge identities (canonical "is this connected?" source) with
  // provider accounts (carries `hasAccessToken` for reauth detection).
  // The unlink button targets the identity id rather than the
  // provider key — matching the new identity-first model where one
  // user could in principle have multiple identities for the same
  // provider (different workspaces, different orgs, etc.).
  const linkedAccounts: Array<LinkedAccount & { identityId: string }> = identities.map((id) => {
    const account = providerAccounts.find((p) => p.provider === id.provider);
    return {
      identityId: id.id,
      provider: id.provider,
      providerUserId: id.providerUserId,
      providerData: account?.providerData ?? null,
      // Sign-in-only providers (Slack, Linear) don't store tokens —
      // an identity row exists without a `provider_accounts` row. We
      // treat the lack of an account as "no token", which the
      // provider-state helper interprets as `connected` because the
      // provider's `requiresStoredToken` is false.
      hasAccessToken: account?.hasAccessToken ?? false,
    };
  });
  // Providers an operator hid from the login screen (`hideOnLogin`) stay out
  // of this list too — offering "Connect Email" here when email sign-in isn't
  // on the login screen would link a credential the user can never sign in
  // with. Two exceptions keep the list honest: the same `?showHidden=true`
  // opt-in that reveals them on the login screen, and any provider the user
  // already has linked (hiding a linked identity would strand it with no way
  // to unlink).
  const showHidden = useShowHiddenProviders();
  const visibleProviders: ProviderEntry[] = Object.entries(configuredProviders)
    .filter(
      ([name, { hidden }]) =>
        !hidden || showHidden || linkedAccounts.some((a) => a.provider === name),
    )
    .map(([name, { label, requiresStoredToken }]) => ({ name, label, requiresStoredToken }));

  const { notificationSettingsSections, otherSettingsSections } = useMemo(() => {
    const notificationSettingsSections = settingsSections.filter((section) =>
      NOTIFICATION_SECTION_IDS.has(section.id),
    );
    const otherSettingsSections = settingsSections.filter(
      (section) => !NOTIFICATION_SECTION_IDS.has(section.id),
    );
    return { notificationSettingsSections, otherSettingsSections };
  }, [settingsSections]);

  const navSections = useMemo(
    () => [
      { id: 'account', label: 'Account', icon: User },
      { id: 'notifications', label: 'Notifications', icon: Bell },
      ...otherSettingsSections.map((section) => ({
        id: section.id,
        label: section.label,
        icon: section.icon,
      })),
      { id: 'appearance', label: 'Appearance', icon: Palette },
    ],
    [otherSettingsSections],
  );

  return (
    <PageLayout maxWidth="full">
      <div className="mx-auto flex w-full max-w-6xl gap-10">
        <div className="min-w-0 flex-1 max-w-4xl">
          <PageHeader
            icon={<Settings className="h-5 w-5" />}
            title="Settings"
            description="Manage your account and preferences"
          />

          {onboardingState?.showRequiredConfigurationBanner && (
            <div className="mt-4">
              <RequiredConfigurationBanner
                progress={onboardingState.progress}
                hasOutstanding={onboardingState.hasOutstanding}
                onDismiss={() => bannerDismiss.mutate()}
              />
            </div>
          )}

          <div className="mt-6 space-y-8">
            {/* Account Section */}
            <div id="account" className="space-y-6 scroll-mt-24">
              <IconSectionHeader icon={User} className="mb-2 gap-3">
                Account
              </IconSectionHeader>

              {/* Profile Information */}
              {user && (
                <SectionCard
                  title="Profile Information"
                  description="Your basic account information"
                  contentClassName="space-y-4"
                >
                  <div className="flex items-center gap-4">
                    <UserAvatar
                      userName={user.name || user.login}
                      avatarUrl={user.avatarUrl ?? undefined}
                      size="lg"
                    />
                    <div>
                      <PanelTitle as="h3">{user.name || user.login}</PanelTitle>
                      <PageDescription className="mt-0">{user.email}</PageDescription>
                    </div>
                  </div>
                  {verificationOutcome && (
                    <EmailVerificationResultNotice outcome={verificationOutcome} />
                  )}
                  {user.emailVerified === false ? (
                    // Only a password account proves its address through the
                    // mailed link, so only it gets the resend button; an OAuth
                    // account clears the notice by signing in with a provider
                    // that verifies email.
                    linkedAccounts.some((a) => a.provider === 'email') ? (
                      <EmailNotVerifiedNotice
                        email={user.email}
                        onSend={api.requestEmailVerification}
                      />
                    ) : (
                      <EmailNotVerifiedNotice />
                    )
                  ) : (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">
                        <Mail className="h-3 w-3 mr-1" />
                        Email Verified
                      </Badge>
                    </div>
                  )}
                </SectionCard>
              )}

              {/* Linked Providers */}
              <SectionCard
                title="Authentication Providers"
                description="You can sign in with any of these providers. Link multiple providers to ensure you never lose access to your account."
              >
                <ProviderAccountList
                  providers={visibleProviders}
                  linkedAccounts={linkedAccounts}
                  onConnect={connect}
                  onUnlink={(provider) => {
                    // Map provider key → identity id. The list is keyed by
                    // provider name (one row per provider in the UI), so
                    // we look up the row's identity to delete the
                    // canonical record.
                    const target = linkedAccounts.find((a) => a.provider === provider);
                    if (!target) return;
                    mutateWithToast(unlinkMutation, target.identityId, {
                      success: `${provider} disconnected`,
                      error: 'Failed to disconnect',
                    });
                  }}
                  unlinkDisabled={linkedAccounts.length <= 1}
                  unlinkPending={unlinkMutation.isPending}
                  connectingProvider={connectingProvider}
                />

                {linkedAccounts.length === 1 && (
                  <Alert className="mt-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      You must have at least one authentication provider linked to your account.
                      Link another provider before removing this one.
                    </AlertDescription>
                  </Alert>
                )}
              </SectionCard>

              <CompanySsoCard alreadyLinked={linkedAccounts.some((a) => a.provider === 'oidc')} />

              {/* Security Notice */}
              {user && (
                <SectionCard title="Account Security">
                  <PageDescription className="mt-0">
                    Each provider you connect can sign you in to this account, even if the provider
                    returns a different email address. Link a backup provider so you never lose
                    access if one of them changes.
                  </PageDescription>
                </SectionCard>
              )}

              {/* Personal Model Providers discovery link — points at
              Configure → Credentials (personal model providers). Kept as a
              link rather than embedded so the credential surface stays
              self-contained. */}
              <SectionCard
                title="Model Providers"
                description="Manage your personal model-provider API keys and subscriptions (Claude Max, Codex). Private to your account."
              >
                <Button asChild variant="outline" size="sm">
                  <Link to={ROUTES.credentials} className="inline-flex items-center gap-2">
                    <KeyRound className="h-4 w-4" />
                    Manage model providers
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              </SectionCard>
            </div>

            <Separator />

            {/* Notifications Section */}
            <div
              id="notifications"
              data-testid="settings-section-notifications"
              className="space-y-6 scroll-mt-24"
            >
              <IconSectionHeader icon={Bell} className="mb-2 gap-3">
                Notifications
              </IconSectionHeader>
              <NotificationsSettingsCard />
              {notificationSettingsSections.map((section) => (
                <Suspense
                  key={section.id}
                  fallback={<div className="text-sm text-muted-foreground">Loading…</div>}
                >
                  <section.component />
                </Suspense>
              ))}
            </div>

            {/* Plugin Settings Sections */}
            {otherSettingsSections.map((section) => {
              const SectionIcon = section.icon;
              return (
                <Fragment key={section.id}>
                  <Separator />
                  <div id={section.id} className="space-y-6 scroll-mt-24">
                    <IconSectionHeader icon={SectionIcon} className="mb-2 gap-3">
                      {section.label}
                    </IconSectionHeader>
                    <Suspense
                      fallback={<div className="text-sm text-muted-foreground">Loading…</div>}
                    >
                      <section.component />
                    </Suspense>
                  </div>
                </Fragment>
              );
            })}

            <Separator />

            {/* Appearance Section */}
            <div id="appearance" className="space-y-6 scroll-mt-24">
              <IconSectionHeader icon={Palette} className="mb-2 gap-3">
                Appearance
              </IconSectionHeader>

              <SectionCard
                title="Appearance Mode"
                description="Choose how Reflex looks. Auto follows your system preference."
              >
                <div className="flex flex-wrap gap-2">
                  {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setTheme(value)}
                      className={cn(
                        'flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors',
                        (theme ?? 'system') === value
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-background hover:bg-muted/50',
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      {label}
                    </button>
                  ))}
                </div>
                <PageDescription className="mt-3 text-xs">
                  Currently using: {resolvedTheme ?? 'system'}
                </PageDescription>
              </SectionCard>

              <SectionCard
                title="Navigation"
                description="Choose how the desktop sidebar opens its sections. Phones keep the bottom tab bar."
              >
                <NavStylePicker value={navStyle} onChange={setNavStyle} />
              </SectionCard>

              <SectionCard
                title="Session Order"
                description="Choose how the agent sidebar orders your sessions."
              >
                <SessionSortPicker value={sessionSort} onChange={setSessionSort} />
              </SectionCard>

              <SectionCard
                title="Color Theme"
                description="Pick a color palette. Your selection syncs across devices."
              >
                <ThemePicker themes={colorThemes} value={colorTheme} onChange={setColorTheme} />
              </SectionCard>
            </div>
          </div>
        </div>
        {/* A div, not <aside>: a complementary landmark nested in the page
            content fails axe `landmark-complementary-is-top-level`. */}
        <div className="hidden w-44 shrink-0 xl:block">
          <div className="sticky top-32">
            <SectionNav sections={navSections} data-testid="settings-section-nav" />
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
